// src/pages/Catalog/ProductCategoriesPage.jsx
//
// Product Categories page
// =======================
//
// PURPOSE
// -------
// Maintain the top-level category structure for products.
//
// Compared to service hierarchy, product category maintenance is intentionally simpler.
// Still, the UX and page structure remain consistent with the rest of the Catalog area.
//

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./ProductCategoriesPage.css";

import {
  fetchProductCategories,
  createProductCategory,
  updateProductCategory,
  toggleProductCategoryActive,
} from "../../services/productCategoriesService";

function asBool(v) {
  return v === true || String(v).trim().toLowerCase() === "true" || String(v).trim() === "1";
}

function toNumberOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function slugify(input) {
  return String(input || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 150);
}

function initialForm() {
  return {
    name_internal: "",
    name_public: "",
    slug: "",
    description_short: "",
    sort_order: 0,
    is_active: true,
    publish_to_website: true,
  };
}

export default function ProductCategoriesPage() {
  const { t } = useTranslation("catalog");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("CREATE");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm());

  const activeCount = useMemo(() => rows.filter((r) => asBool(r.is_active)).length, [rows]);

  async function load() {
    setLoading(true);
    setError("");

    try {
      const data = await fetchProductCategories();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || t("productCategories.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function openCreate() {
    setEditorMode("CREATE");
    setEditingId(null);
    setForm(initialForm());
    setEditorOpen(true);
    setError("");
    setSuccess("");
  }

  function openEdit(row) {
    setEditorMode("EDIT");
    setEditingId(Number(row.id));
    setForm({
      name_internal: row.name_internal || "",
      name_public: row.name_public || "",
      slug: row.slug || "",
      description_short: row.description_short || "",
      sort_order: Number(row.sort_order || 0),
      is_active: asBool(row.is_active),
      publish_to_website: asBool(row.publish_to_website),
    });
    setEditorOpen(true);
    setError("");
    setSuccess("");
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditingId(null);
    setForm(initialForm());
  }

  function handleNamePublicBlurAutoSlug() {
    setForm((prev) => {
      if ((prev.slug || "").trim() !== "") return prev;
      return { ...prev, slug: slugify(prev.name_public) };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        ...form,
        sort_order: toNumberOrZero(form.sort_order),
      };

      if (editorMode === "CREATE") {
        await createProductCategory(payload);
        setSuccess(t("productCategories.messages.created"));
      } else {
        await updateProductCategory(editingId, payload);
        setSuccess(t("productCategories.messages.updated"));
      }

      closeEditor();
      await load();
    } catch (e2) {
      setError(e2?.message || t("productCategories.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(row) {
    setError("");
    setSuccess("");

    try {
      await toggleProductCategoryActive(row.id, !asBool(row.is_active));
      setSuccess(
        t("productCategories.messages.statusChanged", {
          status: asBool(row.is_active)
            ? t("productCategories.status.deactivated")
            : t("productCategories.status.activated"),
        })
      );
      await load();
    } catch (e) {
      setError(e?.message || t("productCategories.messages.statusChangeFailed"));
    }
  }

  return (
    <div className="pc-page">
      <div className="pc-page__inner">
        <div className="pc-page__header">
          <div>
            <h1>{t("productCategories.title")}</h1>
            <p>
              {t("productCategories.subtitle")}
            </p>
          </div>

          <div className="pc-page__headerActions">
            <button type="button" className="pc-btn pc-btn--primary" onClick={openCreate}>
              {t("productCategories.actions.add")}
            </button>
          </div>
        </div>

        <div className="pc-page__card">
          <div className="pc-page__meta">
            <span>{t("productCategories.meta.total", { count: rows.length })}</span>
            <span>{t("productCategories.meta.active", { count: activeCount })}</span>
          </div>

          {error ? <div className="pc-alert pc-alert--error">{error}</div> : null}
          {success ? <div className="pc-alert pc-alert--success">{success}</div> : null}

          {editorOpen && (
            <form className="pc-editor" onSubmit={handleSubmit}>
              <div className="pc-editor__grid">
                <div className="pc-field">
                  <label>{t("productCategories.fields.internalName")}</label>
                  <input
                    value={form.name_internal}
                    onChange={(e) => setForm((prev) => ({ ...prev, name_internal: e.target.value }))}
                    required
                  />
                </div>

                <div className="pc-field">
                  <label>{t("productCategories.fields.publicName")}</label>
                  <input
                    value={form.name_public}
                    onChange={(e) => setForm((prev) => ({ ...prev, name_public: e.target.value }))}
                    onBlur={handleNamePublicBlurAutoSlug}
                    required
                  />
                </div>

                <div className="pc-field">
                  <label>{t("productCategories.fields.slug")}</label>
                  <input
                    value={form.slug}
                    onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                    placeholder={t("productCategories.placeholders.autoSlug")}
                  />
                </div>

                <div className="pc-field">
                  <label>{t("productCategories.fields.sortOrder")}</label>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm((prev) => ({ ...prev, sort_order: e.target.value }))}
                  />
                </div>

                <div className="pc-field pc-field--full">
                  <label>{t("productCategories.fields.shortDescription")}</label>
                  <input
                    value={form.description_short}
                    onChange={(e) => setForm((prev) => ({ ...prev, description_short: e.target.value }))}
                  />
                </div>
              </div>

              <div className="pc-editor__checks">
                <label>
                  <input
                    type="checkbox"
                    checked={!!form.is_active}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                  />
                  <span>{t("productCategories.fields.active")}</span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={!!form.publish_to_website}
                    onChange={(e) => setForm((prev) => ({ ...prev, publish_to_website: e.target.checked }))}
                  />
                  <span>{t("productCategories.fields.publishToWebsite")}</span>
                </label>
              </div>

              <div className="pc-editor__actions">
                <button type="submit" className="pc-btn pc-btn--primary" disabled={saving}>
                  {saving
                    ? t("productCategories.actions.saving")
                    : editorMode === "CREATE"
                      ? t("productCategories.actions.create")
                      : t("productCategories.actions.saveChanges")}
                </button>
                <button type="button" className="pc-btn" onClick={closeEditor} disabled={saving}>
                  {t("productCategories.actions.cancel")}
                </button>
              </div>
            </form>
          )}

          <div className="pc-tableArea">
            <div className="pc-tableWrap">
              <table className="pc-table">
                <thead>
                  <tr>
                    <th className="pc-left">{t("productCategories.columns.publicName")}</th>
                    <th className="pc-left">{t("productCategories.columns.slug")}</th>
                    <th className="pc-left">{t("productCategories.columns.sort")}</th>
                    <th className="pc-left">{t("productCategories.columns.active")}</th>
                    <th className="pc-left">{t("productCategories.columns.publish")}</th>
                    <th className="pc-right">{t("productCategories.columns.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && rows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="pc-empty">{t("productCategories.empty")}</td>
                    </tr>
                  ) : null}

                  {rows.map((row) => (
                    <tr key={row.id} className={!asBool(row.is_active) ? "pc-rowMuted" : ""}>
                      <td className="pc-left">{row.name_public}</td>
                      <td className="pc-left"><code>{row.slug}</code></td>
                      <td className="pc-left">{row.sort_order}</td>
                      <td className="pc-left">{asBool(row.is_active) ? t("productCategories.boolean.yes") : t("productCategories.boolean.no")}</td>
                      <td className="pc-left">{asBool(row.publish_to_website) ? t("productCategories.boolean.yes") : t("productCategories.boolean.no")}</td>
                      <td className="pc-right">
                        <div className="pc-actions">
                          <button type="button" className="pc-btn pc-btn--small" onClick={() => openEdit(row)}>
                            {t("productCategories.actions.edit")}
                          </button>
                          <button
                            type="button"
                            className="pc-btn pc-btn--small"
                            onClick={() => handleToggleActive(row)}
                          >
                            {asBool(row.is_active)
                              ? t("productCategories.actions.deactivate")
                              : t("productCategories.actions.activate")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

